import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {environment} from '../../environments/environment';

const SEARCH_URL = 'https://atlas.microsoft.com/search/address/json';
const REVERSE_SEARCH_URL = 'https://atlas.microsoft.com/search/address/reverse/json';

interface AzureSearchAddressResponse {
    results: {position: {lat: number; lon: number}}[];
}

interface AzureReverseSearchAddressResponse {
    addresses: {address: {freeformAddress?: string; municipality?: string}}[];
}

export interface ReverseGeocodeResult {
    address: string;
    city: string;
}

/**
 * Wraps the Azure Maps Search API to convert between a free-form address and coordinates,
 * so the project location card ({@link AzureMapComponent}) can keep its pin in sync with
 * the address fields and vice versa.
 */
@Injectable({providedIn: 'root'})
export class AzureGeocodingService {
    private readonly httpClient = inject(HttpClient);

    public geocodeAddress(address: string, city: string, country: string): Observable<[number, number] | null> {
        return this.httpClient.get<AzureSearchAddressResponse>(SEARCH_URL, {
            params: {
                'api-version': '1.0',
                'subscription-key': environment.azureMapsKey,
                limit: '1',
                query: [address, city, country].filter(Boolean).join(', ')
            }
        }).pipe(
            map((response) => {
                const position = response.results?.[0]?.position;
                return position ? [position.lat, position.lon] as [number, number] : null;
            }),
            catchError(() => of(null))
        );
    }

    public reverseGeocode(latitude: number, longitude: number): Observable<ReverseGeocodeResult | null> {
        return this.httpClient.get<AzureReverseSearchAddressResponse>(REVERSE_SEARCH_URL, {
            params: {
                'api-version': '1.0',
                'subscription-key': environment.azureMapsKey,
                query: `${latitude},${longitude}`
            }
        }).pipe(
            map((response) => {
                const address = response.addresses?.[0]?.address;
                return address ? {address: address.freeformAddress ?? '', city: address.municipality ?? ''} : null;
            }),
            catchError(() => of(null))
        );
    }
}
